import React, { useEffect, useMemo, useRef, useState } from 'react';
import { func, string } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  createSlug,
} from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';

import {
  NamedRedirect,
  Page,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutSingleColumn,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../components';
import { PortfolioWorkForm } from '../../forms';
import { TopbarContainer } from '../../containers';
import {
  loadData,
  createPortfolioWorkDraft,
  updatePortfolioWork,
  publishPortfolioWork,
  updateImageOrder,
  uploadPortfolioWorkImage,
  removePortfolioWorkImage,
} from './EditPortfolioWorkPage.duck';

import css from './EditPortfolioWorkPage.css';

const { UUID } = sdkTypes;

export const EditPortfolioWorkPageComponent = props => {
  const {
    history,
    params,
    getOwnListing,
    intl,
    scrollingDisabled,
    currentUser,
    onClose,
    onDelete,
    onImageUpload,
    onImageRemove,
    onImageOrderChange,
    portfolioWorkId,
    page,
    onCreatePortfolioWorkDraft,
    onUpdatePortfolioWork,
    onPublishPortfolioWork,
    skillOptions,
    toolOptions,
  } = props;

  const { id, type } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewPortfolioWorkFlow = isNewURI;

  // For new portfolio work flow, prioritize URL parameter over submitted portfolio work ID
  const workId =
    isNewPortfolioWorkFlow && id
      ? new UUID(id)
      : page.submittedPortfolioWorkId || (id ? new UUID(id) : null);
  const currentPortfolioWork = ensureOwnListing(getOwnListing(workId));
  const { state: currentPortfolioWorkState, title, publicData } = currentPortfolioWork.attributes;

  const isPastDraft =
    currentPortfolioWorkState && currentPortfolioWorkState !== LISTING_STATE_DRAFT;
  const shouldRedirect = isNewPortfolioWorkFlow && workId && isPastDraft;

  const pageTitle = intl.formatMessage({ id: 'EditPortfolioWorkPage.titleCreateListing' });

  const isCustomer =
    currentUser && currentUser.attributes.profile.publicData.account_role === 'customer';
  const canCreateListings = !isCustomer;

  const showPublishButton = isNewURI || isDraftURI;

  const submitButtonText =
    isNewURI || isDraftURI ? (
      <FormattedMessage id="EditPortfolioWorkPage.submitAsDraftButton" />
    ) : (
      <FormattedMessage id="EditPortfolioWorkPage.submitButton" />
    );

  const saveButtonText = showPublishButton ? (
    <FormattedMessage id="EditPortfolioWorkPage.submitAsDraftButton" />
  ) : (
    <FormattedMessage id="EditPortfolioWorkPage.saveButtonText" />
  );

  // State for cancel confirmation modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Unsaved-changes guard: warn before leaving the page with unsaved edits.
  // Dirty state comes from the form (FormSpy) and from image changes that
  // live outside the form (uploads not yet attached, pending removals).
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveLocation, setLeaveLocation] = useState(null);
  const bypassUnsavedGuardRef = useRef(false);

  const hasUnsavedChanges =
    isFormDirty || (page.imageOrder || []).length > 0 || (page.removedImageIds || []).length > 0;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  // Block in-app navigation (back button, topbar links) while there are
  // unsaved edits, and show our own confirmation modal instead
  useEffect(() => {
    const unblock = history.block(location => {
      if (bypassUnsavedGuardRef.current || !hasUnsavedChangesRef.current) {
        return true;
      }
      setLeaveLocation(location);
      setLeaveModalOpen(true);
      return false;
    });
    return unblock;
  }, [history]);

  // Browser-level warning on refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (hasUnsavedChangesRef.current && !bypassUnsavedGuardRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Always call hooks before any early returns!
  const initialValues = useMemo(
    () => ({
      title,
      ...publicData,
      imageRadius: publicData?.imageRadius
        ? 'true'
        : publicData?.imageRadius === false
        ? 'false'
        : 'true',
      relatedWorkExperienceCustomer: publicData?.relatedWorkExperienceCustomer || '',
      relatedWorkCustomerType: publicData?.relatedWorkCustomerType || '',
      relatedWorkExperienceAgency: publicData?.relatedWorkExperienceAgency || '',
      privateWork: publicData?.privateWork
        ? 'true'
        : publicData?.privateWork === false
        ? 'false'
        : '',
      jobContinues: publicData?.jobContinues,
      startMonth: publicData?.startMonth || '',
      startYear: publicData?.startYear || '',
      endMonth: publicData?.endMonth || '',
      endYear: publicData?.endYear || '',
      experience: publicData?.experience?.length
        ? publicData.experience.map(each => ({
            category: {
              key: each.category,
              label: (() => {
                const skill = skillOptions.find(e => e.key === each.category);
                if (skill?.label) {
                  return typeof skill.label === 'string'
                    ? skill.label
                    : intl.formatMessage({ id: skill.label.props.id });
                }
                return '';
              })(),
            },
          }))
        : [{}],
      tools: publicData?.tools?.length
        ? publicData.tools.map(each => ({
            category: {
              key: each.category,
              label: (() => {
                const tool = toolOptions.find(e => e.key === each.category);
                if (tool?.label) {
                  return typeof tool.label === 'string'
                    ? tool.label
                    : intl.formatMessage({ id: tool.label.props.id });
                }
                return '';
              })(),
            },
          }))
        : [{}],
    }),
    [title, publicData, intl, skillOptions, toolOptions]
  );

  // Now you can have early returns below
  if (!canCreateListings) {
    return <NamedRedirect name="LandingPage" />;
  }
  if (shouldRedirect) {
    const listingSlug = currentPortfolioWork
      ? createSlug(currentPortfolioWork.attributes.title)
      : null;

    const redirectProps = {
      name: 'PortfolioWorkPageCanonical',
      params: {
        id: workId.uuid,
        slug: listingSlug,
      },
    };

    return <NamedRedirect {...redirectProps} />;
  }

  const currentPortfolioWorkImages =
    currentPortfolioWork && currentPortfolioWork.images ? currentPortfolioWork.images : [];

  // Images not yet connected to the listing
  const imageOrder = page.imageOrder || [];
  const unattachedImages = imageOrder.map(i => page.images[i]);

  const allImages = currentPortfolioWorkImages.concat(unattachedImages);
  const removedImageIds = page.removedImageIds || [];
  const images = allImages.filter(img => {
    const imgId = img.id?.uuid || img.id;
    return !removedImageIds.some(removedId => {
      const removedIdStr = removedId?.uuid || removedId;
      return imgId === removedIdStr;
    });
  });

  const imageIds = images => {
    return images ? images.map(img => img.imageId || img.id) : null;
  };

  const handleSubmit = async values => {
    const { images: updatedImages, submitAction, ...otherValues } = values;

    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const updateValuesWithImages = { ...otherValues, ...imageProperty };

    try {
      let result;
      if (isNewPortfolioWorkFlow) {
        result = await onCreatePortfolioWorkDraft(updateValuesWithImages);
      } else {
        const updateValues = { ...updateValuesWithImages, id: currentPortfolioWork.id };
        result = await onUpdatePortfolioWork(updateValues);
      }

      // Save as draft: the work is stored (possibly incomplete) and the user
      // returns to their portfolio list to finish it later
      if (submitAction === 'saveDraft') {
        if (result?.data?.data?.id) {
          bypassUnsavedGuardRef.current = true;
          history.push('/profile-settings');
        }
        return;
      }

      if (submitAction === 'publish') {
        result = await onPublishPortfolioWork(result.data.data.id);
      }

      // Redirect on success
      if (result && result.data && result.data.data.id) {
        const state = result.data.data.attributes.state;
        const listingSlug = createSlug(result.data.data.attributes.title);
        bypassUnsavedGuardRef.current = true;
        if (state === 'draft') {
          history.push(`/work/${listingSlug}/${result.data.data.id.uuid}/draft/preview`);
        } else if (state === 'published') {
          history.push(`/work/${listingSlug}/${result.data.data.id.uuid}/`);
        }
      }
    } catch (e) {
      // Handle error if needed
      console.error('Submit failed', e);
    }
  };

  const handleCancel = e => {
    e.preventDefault();
    setCancelModalOpen(true);
  };

  const confirmCancel = () => {
    setCancelModalOpen(false);
    // The user explicitly discarded their edits — don't double-prompt
    bypassUnsavedGuardRef.current = true;

    if (!title) {
      // If no title, redirect to the work list
      history.push('/profile-settings');
      return;
    }
    const slug = createSlug(title);
    if (showPublishButton) {
      history.push(`/work/${slug}/${workId.uuid}/draft/preview`);
    } else {
      history.push(`/work/${slug}/${workId.uuid}`);
    }
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
  };

  const confirmLeave = () => {
    setLeaveModalOpen(false);
    bypassUnsavedGuardRef.current = true;
    if (leaveLocation) {
      history.push(leaveLocation);
    }
  };

  const closeLeaveModal = () => {
    setLeaveModalOpen(false);
    setLeaveLocation(null);
  };

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer className={css.topbar} />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PortfolioWorkForm
            initialValues={initialValues}
            onSubmit={values => {
              handleSubmit(values);
            }}
            onClose={onClose}
            onDelete={onDelete}
            onUploadImage={onImageUpload}
            onRemoveImage={onImageRemove}
            onImageOrderChange={onImageOrderChange}
            onUpdatePortfolioWork={onUpdatePortfolioWork}
            portfolioWorkId={portfolioWorkId}
            images={images}
            imageOrder={imageOrder}
            skillOptions={skillOptions}
            toolOptions={toolOptions}
            currentUser={currentUser}
            showPublishButton={showPublishButton}
            submitButtonText={submitButtonText}
            saveButtonText={saveButtonText}
            onManageDisableScrolling={props.onManageDisableScrolling}
            handleCancel={handleCancel}
            isNewPortfolioWorkFlow={isNewPortfolioWorkFlow}
            onDirtyChange={setIsFormDirty}
          />
        </LayoutWrapperMain>
      </LayoutSingleColumn>

      <Modal
        id="EditPortfolioWorkPage.cancelConfirmation"
        isOpen={cancelModalOpen}
        onClose={closeCancelModal}
        onManageDisableScrolling={props.onManageDisableScrolling}
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        <div className={css.cancelModalContent}>
          <h1 className={css.cancelModalTitle}>
            <FormattedMessage id="EditPortfolioWorkPage.cancelConfirmationTitle" />
          </h1>
          <p className={css.cancelModalMessage}>
            <FormattedMessage id="EditPortfolioWorkPage.cancelConfirmationMessage" />
          </p>
          <div className={css.cancelModalButtons}>
            <SecondaryButton onClick={closeCancelModal} className={css.cancelModalButton}>
              <FormattedMessage id="EditPortfolioWorkPage.continueEditing" />
            </SecondaryButton>
            <PrimaryButton onClick={confirmCancel} className={css.cancelModalButton}>
              <FormattedMessage id="EditPortfolioWorkPage.confirmCancel" />
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        id="EditPortfolioWorkPage.unsavedChanges"
        isOpen={leaveModalOpen}
        onClose={closeLeaveModal}
        onManageDisableScrolling={props.onManageDisableScrolling}
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        <div className={css.cancelModalContent}>
          <h1 className={css.cancelModalTitle}>
            <FormattedMessage id="EditPortfolioWorkPage.unsavedChangesTitle" />
          </h1>
          <p className={css.cancelModalMessage}>
            <FormattedMessage id="EditPortfolioWorkPage.unsavedChangesMessage" />
          </p>
          <div className={css.cancelModalButtons}>
            <SecondaryButton onClick={confirmLeave} className={css.cancelModalButton}>
              <FormattedMessage id="EditPortfolioWorkPage.leaveWithoutSaving" />
            </SecondaryButton>
            <PrimaryButton onClick={closeLeaveModal} className={css.cancelModalButton}>
              <FormattedMessage id="EditPortfolioWorkPage.continueEditing" />
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

EditPortfolioWorkPageComponent.defaultProps = {
  className: null,
  rootClassName: null,
};

EditPortfolioWorkPageComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onManageDisableScrolling: func,
  onSubmit: func,
  onClose: func,
  onDelete: func,
  onImageUpload: func,
  onImageRemove: func,
  onImageOrderChange: func,
  onUpdatePortfolioWork: func,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { skillOptions, toolOptions } = state.SkillsAndTools;
  const page = state.EditPortfolioWorkPage;

  const getOwnListing = id => {
    const listings = getMarketplaceEntities(state, [{ id, type: 'ownListing' }]);

    return listings.length === 1 ? listings[0] : null;
  };
  return {
    currentUser,
    skillOptions,
    toolOptions,
    page,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
  };
};
const mapDispatchToProps = dispatch => {
  return {
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onImageUpload: image => dispatch(uploadPortfolioWorkImage(image)),
    onImageRemove: imageId => dispatch(removePortfolioWorkImage(imageId)),
    onImageOrderChange: (imageId, newOrder) => dispatch(updateImageOrder(imageId, newOrder)),
    onCreatePortfolioWorkDraft: portfolioWorkDraft =>
      dispatch(createPortfolioWorkDraft(portfolioWorkDraft)),
    onUpdatePortfolioWork: portfolioWorkData => dispatch(updatePortfolioWork(portfolioWorkData)),
    onPublishPortfolioWork: portfolioWorkId => dispatch(publishPortfolioWork(portfolioWorkId)),
  };
};

const EditPortfolioWorkPage = compose(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(EditPortfolioWorkPageComponent);

EditPortfolioWorkPage.loadData = loadData;

export default EditPortfolioWorkPage;
